import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { useHover } from '~/src/hooks/use-hover';
import { useMatchMedia } from '~/src/hooks/use-match-media.hook';
import { getTooltipPlacement } from '~/src/components/form-components/tooltip/tooltip.utils';
import { MediaWidthEnum } from '~/src/models/enums/media-width.enum';
import { SvgQuestionMark } from '~/src/components/svg-components/question-mark';
import { TooltipDirectionEnum } from '~/src/components/form-components/tooltip/tooltip.types';
import type { TooltipInterface } from '~/src/components/form-components/tooltip/tooltip.types';
import * as Styled from './tooltip.styles';

/**
 * Used in Module Federation
 */

const Tooltip: FC<TooltipInterface> = ({
    hintText,
    width,
    direction,
    callback,
    className,
    isHover,
    onMouseOver,
    isControlled,
}) => {
    const [isVisible, setIsVisible] = useState(isHover);
    const [isTabletWide] = useMatchMedia(MediaWidthEnum.tabletWide, { withoutSSR: true });
    const svgQuestionMarkRef = useRef<SVGSVGElement | null>(null);
    const tooltipTextRef = useRef<HTMLDivElement | null>(null);
    const tooltipPlacement = useRef<TooltipDirectionEnum>(TooltipDirectionEnum.TOP);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const timeoutId = useRef<ReturnType<typeof setTimeout>>();

    const isInnerHover = useHover(tooltipRef);

    const handleMouseOver = () => {
        onMouseOver && onMouseOver();
        isHover && setIsVisible(true);
    };

    const onClickHandler = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (event.target instanceof HTMLAnchorElement) {
            callback && callback();
        }
    };

    useEffect(() => {
        setIsVisible(isHover);
    }, [isHover]);

    useEffect(() => {
        if (isVisible && !isInnerHover || isVisible && isTabletWide) {
            timeoutId.current = setTimeout(() => {
                setIsVisible(false);
            }, 3000);
        }

        return () => clearTimeout(timeoutId.current);
    }, [isVisible, isInnerHover, isTabletWide]);

    useEffect(() => {
        if (!direction && svgQuestionMarkRef?.current && tooltipTextRef?.current) {
            const { left, right } = svgQuestionMarkRef.current.getBoundingClientRect();
            const tooltipWidth = tooltipTextRef.current.offsetWidth;

            const svgPlacementParams = {
                distanceToLeft: left,
                distanceToRight: document.documentElement.clientWidth - right,
            };

            tooltipPlacement.current = getTooltipPlacement(svgPlacementParams, tooltipWidth);
        }
    }, [direction]);

    return (
        <Styled.TooltipWrap className={className} isHover={isVisible} isControlled={isControlled} ref={tooltipRef}>
            <SvgQuestionMark iconRef={svgQuestionMarkRef} onMouseOver={handleMouseOver} />
            <Styled.TooltipText
                dangerouslySetInnerHTML={{ __html: hintText }}
                direction={direction || tooltipPlacement.current}
                width={width}
                onClick={onClickHandler}
                ref={tooltipTextRef}
            />
        </Styled.TooltipWrap>
    );
};

export default Tooltip;
